import React from 'react';
import PropTypes from 'prop-types';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from "@material-ui/core/Paper";
import { withStyles } from '@material-ui/core/styles';

import FormStep from '../../components/form/FormStep';
import FormField from '../../components/form/FormField';
import VersionFormField from '../../components/form/VersionFormField';
import Form from '../../components/form/Form';
import Model from '../../api/Model';
import VersionChangesField from '../../components/form/VersionChangesField';
import FactoryFormField from "../../components/form/FactoryFormField";
import TechnologyFormField from "../../components/form/TechnologyFormField";
import ScenarioFormField from "../../components/form/ScenarioFormField";
import { cycle } from "../../store/Cycle";
import Scenario from "../../api/Scenario";
import Version from "../../api/Version";
import Technology from "../../api/Technology";
import Factory from "../../api/Factory";

import { VERSION_TYPES, ALL_VERSIONS, ENABLED_FIELDS } from '../../labels';

const styles = theme => ({
  fieldsetTitle: {
    marginTop: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
  },
});


@withStyles(styles)
@observer
class ScenarioForm extends React.Component {
  static propTypes = {
    instance: PropTypes.instanceOf(Model).isRequired,
    refScenarioId: PropTypes.any,
    isEditMode: PropTypes.bool
  };

  static versionTypes = VERSION_TYPES;

  @observable versions = [];
  @observable technologies = [];
  @observable factories = [];

  async componentDidMount() {
    this.versions = await Version.getList({});
    this.technologies = await Technology.getList({});
    this.factories = await Factory.getList({});
  }

  @computed get allTechnologyIds() {
    return this.technologies.map(technology => technology.name).join(', ');
  }

  @computed get allFactoryIds() {
    return this.factories.map(factory => factory.id).join(', ');
  }

  @computed get technologyFamilies() {
    const { instance } = this.props;
    const technologyFamilies = this.technologies.filter(
      technology => instance.technologies.includes(technology.name)
    ).map(technology => technology.family);
    // Drops duplicates
    return technologyFamilies.filter((family, index) => technologyFamilies.indexOf(family) === index);
  }

  @computed get enabledFields() {
    const { instance } = this.props;
    // const { enabledFields } = labels.enabledFields;
    if (instance.id !== null) {
      return ENABLED_FIELDS.NO_ID;
    }
    if (instance.isBase || instance.isBaseOptimization) {
      return ENABLED_FIELDS.BASE
    }
    if (instance.isReducedScopeSimulation) {
      return ENABLED_FIELDS.REDUCED_SCOPE
    }
    return null;
  }

  isDisabledField(fieldName) {
    const enabledFields = this.enabledFields;
    if (enabledFields === null) {
      return false;
    }
    return enabledFields.indexOf(fieldName) === -1;
  }

  @action onTypeChange = (event, instance) => {
    // if it's edit mode, then do nothing
    if (this.props.isEditMode) {
      return;
    }
    if (instance.isBase) {
      instance.resetToDefaults(this.enabledFields, true);
    } else if (instance.isBaseOptimization) {
      instance.resetToDefaults(this.enabledFields, true);
      if (instance.optimization_constraints_techno_list_move === '') {
        instance.optimization_constraints_techno_list_move = this.allTechnologyIds;
      }
      if (instance.optimization_constraints_plant_list_move === '') {
        instance.optimization_constraints_plant_list_move = this.allFactoryIds;
      }
      if (instance.optimization_moves_capacity === 0) {
        instance.optimization_moves_capacity = 15;
      }
      if (instance.optimization_max_fg_transfers === 0) {
        instance.optimization_max_fg_transfers = 5;
      }
      if (instance.optimization_max_sfg_transfers === 0) {
        instance.optimization_max_sfg_transfers = 5;
      }
    } else if (instance.isSimulation) {
      /*  
      *   Passing all the version names to 'excludedFields' as this was updating all the versions to the latest certified ones 
      *   when user were changing the scenario from Validated to Simulation and users were complaining about this ( JIRA -> GRS-156 )
      */
      instance.parent = null;
      const allVersions = ALL_VERSIONS;
      instance.resetToDefaults(allVersions.concat(this.enabledFields), true);
    } else if (instance.isReducedScopeSimulation) {
      this.onParentChange(event, instance);
      this.onFactoryChange(event, instance);
      this.onTechnologyChange(event, instance);
    }
  };

  onParentChange = async (event, instance) => {
    if (instance.parent) {
      const parent = await Scenario.asyncGetForId(instance.parent);
      instance.prediction_duration = parent.prediction_duration;
      instance.optimization_neg_stocks_not_to_recover = parent.optimization_neg_stocks_not_to_recover;
      for (const [field, value] of Object.entries(parent)) {
        if (field.endsWith('_version')) {
          instance[field] = value;
        }
      }
    }
  };

  @action onFactoryChange = (event, instance) => {
    instance.optimization_constraints_plant_list_move = instance.factories.join(', ');
  };

  @action onTechnologyChange = (event, instance) => {
    instance.optimization_constraints_techno_list_move = instance.technologies.join(', ');

    // Sets the input files with the available ones.
    for (const [fieldName, versionType] of this.constructor.versionTypes) {
      const versions = this.getVersions(versionType);
      if (versions.filter(version => version.id === instance[fieldName]).length === 0) {
        const closestVersions = versions.sort((version1, version2) => {
          const family1 = version1.technology_family;
          const family2 = version2.technology_family;
          return (
            (version2.certified - version1.certified)                    // Certified first
            || Math.sign(version2.cycle.date - version1.cycle.date)   // More recent versions first
            || (family1 === family2 ? 0 : (family1 > family2 ? 1 : -1))  // “All” technology family first
            || Math.sign(version2.id - version1.id)                   // Recent IDs first
          );
        });
        instance[fieldName] = closestVersions[0].id;
      }
    }

    // Change for GRS-228: Resetting the factories to "ALL" when technology is changed to "ALL"
    const allFamilies = ["first_diet", "powders", "liquids"];
    if ( this.technologyFamilies.length === allFamilies.length && this.technologyFamilies.every((val, idx) => val === allFamilies[idx]) )
      instance.factories = this.allFactoryIds.split(", ").map( factoryID => parseInt(factoryID));
  };

  get basicInformationStep() {
    const { instance, classes } = this.props;
    return (
      <FormStep label="Basic information">
        <Grid container direction="column" spacing={2} wrap="nowrap">
          <Grid item>
            <Paper className={classes.paper}>
              <Grid container direction="column" spacing={2} wrap="nowrap">
                <Grid item container spacing={2}>
                  <Grid item xs={6}>
                    <FormField name="type" onChange={this.onTypeChange} fullWidth />
                  </Grid>
                  <Grid item xs={3}>
                    <FormField name="cycle_year" fullWidth />
                  </Grid>
                  <Grid item xs={3}>
                    <FormField name="cycle_month" fullWidth />
                  </Grid>
                </Grid>
                <Grid item>
                  <FormField name="name" fullWidth />
                </Grid>
                <Grid item>
                  <FormField name="description" fullWidth area />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item>
            <Paper className={classes.paper}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <TechnologyFormField name="technologies" fullWidth onChange={this.onTechnologyChange} />
                </Grid>
                <Grid item xs={6} sm={4} style={instance.isReducedScopeSimulation ? {} : { display: 'none' }}>
                  <FactoryFormField name="factories" fullWidth onChange={this.onFactoryChange} />
                </Grid>
                <Grid item xs={6} sm={4} style={instance.isReducedScopeSimulation ? {} : { display: 'none' }}>
                  <ScenarioFormField
                    name="parent"
                    fullWidth
                    filters={{
                      ...cycle.params, parent__isnull: true, status: Scenario.SUCCESS,
                      gurobi_model_file__isnull: false, gurobi_solution_file__isnull: false,
                    }}
                    onChange={this.onParentChange}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </FormStep>
    );
  }

  @computed get familyVersions() {
    const technologyFamilies = this.technologyFamilies;
    return this.versions.filter(version =>
      version.technology_family === 'all' || technologyFamilies.includes(version.technology_family)
    );
  }

  getVersions = type => {
    return this.familyVersions.filter(version => version.type === type);
  };

  get inputsStep() {
    const { classes } = this.props;
    return (
      <FormStep label="Input files">
        <Paper className={classes.paper}>
          <Grid container spacing={2}>
          {this.constructor.versionTypes.map(([name, type]) => 
              <Grid item md={6} xs={12} key={name}>
                <VersionFormField
                  name={name}
                  versions={this.getVersions(type)}
                  disabled={this.isDisabledField(name)}
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </FormStep>
    );
  }

  get inputChangesStep() {
    return (
      <FormStep label="Guided mode">
        <VersionChangesField
          name="version_changes"
          disabled={this.isDisabledField('version_changes')}
          refScenarioId={this.props.refScenarioId}
        />
      </FormStep>
    );
  }

  get advancedSettingsStep() {
    const { classes } = this.props;
    const renderFields = fields => fields.map(fieldName =>
      <Grid item key={fieldName} lg={3} md={6} xs={12}>
        <FormField
          name={fieldName}
          resetValue
          disabled={this.isDisabledField(fieldName)}
          fullWidth
        />
      </Grid>
    );
    return (
      <FormStep label="Advanced settings">
        <Grid container direction="column" spacing={2} wrap="nowrap">
          <Grid item>
            <Paper className={classes.paper}>
              <Grid item container>
                <Typography variant="h6" className={classes.fieldsetTitle} gutterBottom>
                  Duration & Short-term - Mode A & Mode B
                </Typography>
              </Grid>
              <Grid item container spacing={2} alignItems="center">
                <Grid item lg={3} md={6} xs={12}>
                  <FormField
                    name="prediction_duration"
                    resetValue
                    disabled={this.isDisabledField('prediction_duration')}
                    fullWidth
                  />
                </Grid>
                <Grid item lg={9} md={6} xs={12}>
                  <FormField
                    name="optimization_neg_stocks_not_to_recover"
                    resetValue
                    disabled={this.isDisabledField('optimization_neg_stocks_not_to_recover')}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item>
            <Paper className={classes.paper}>
              <Grid item container>
                <Typography variant="h6" className={classes.fieldsetTitle} gutterBottom>
                  Moves - Mode B
                </Typography>
              </Grid>
              <Grid item container spacing={2} alignItems="center">
                {renderFields([
                  'optimization_constraints_min_month_move',
                  'optimization_constraints_max_month_move',
                ])}
                <Grid item xs={12}>
                  <FormField
                    name="optimization_constraints_techno_list_move"
                    resetValue={this.allTechnologyIds}
                    resetTitle="Set to full list"
                    disabled={this.isDisabledField('optimization_constraints_techno_list_move')}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormField
                    name="optimization_constraints_plant_list_move"
                    resetValue={this.allFactoryIds}
                    resetTitle="Set to full list"
                    disabled={this.isDisabledField('optimization_constraints_plant_list_move')}
                    fullWidth
                  />
                </Grid>
                {renderFields([
                  'optimization_moves_capacity',
                  'optimization_move_capacity_step',
                  'optimization_max_fg_transfers',
                  'optimization_max_sfg_transfers',
                ])}
              </Grid>
            </Paper>
          </Grid>
          <Grid item>
            <Paper className={classes.paper}>
              <Grid item container>
                <Typography variant="h6" className={classes.fieldsetTitle} gutterBottom>
                  Objective function parameters - Mode A & Mode B
                </Typography>
              </Grid>
              <Grid item container spacing={2} alignItems="center">
                {renderFields([
                  'optimization_above_max_alpha',
                  'optimization_below_target_alpha',
                  'optimization_constraints_lead_times',
                  'optimization_add_innovations',
                ])}
          </Grid>
            </Paper>
          </Grid>
        </Grid>
      </FormStep>
    );
  }

  render() {
    const { instance } = this.props;
    return (
      <Form instance={instance} redirectPath={instance => instance.detailUrl}>
        {this.basicInformationStep}
        {this.inputsStep}
        {this.inputChangesStep}
        {this.advancedSettingsStep}
      </Form>
    );
  }
}


export default ScenarioForm;
